import React, { useEffect, useState } from 'react';
import styled from 'styled-components';
import { ChevronDownIcon, ChevronUpIcon } from '@rsos/assets/icons';
import { Box } from '@rsos/base-components';
import {
  COMPANION_SECONDARY_RASTER_ID_SUFFIX,
  gisLayerFor,
  type GISStateLayer,
} from '@rsos/reduxDataStore';

const MIN_STACKING_ORDER = -9;

/** Map internal stackingorder (0-based) to a display level with no 0th floor. */
export function stackingOrderToDisplayLevel(order: number): number {
  return order >= 0 ? order + 1 : order;
}

/**
 * Map a typed display level back to internal stackingorder.
 * Display 0 is invalid (no 0th floor). Returns null when the input is not a
 * usable integer level.
 */
export function displayLevelToStackingOrder(
  displayLevel: number,
): number | null {
  if (!Number.isInteger(displayLevel) || displayLevel === 0) {
    return null;
  }
  const order = displayLevel > 0 ? displayLevel - 1 : displayLevel;
  if (order < MIN_STACKING_ORDER) {
    return null;
  }
  return order;
}

const Wrapper = styled(Box)`
  margin-bottom: 4px;
`;

const Row = styled(Box).attrs({
  horizontal: true,
  align: 'center',
})`
  align-items: center;
  flex-wrap: wrap;
  gap: 6px;
`;

const Label = styled.span`
  font-size: 12px;
  font-weight: 600;
  color: ${({ theme }) => theme.colors.secondaryText};
`;

const LevelStepper = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 3px;
  padding: 3px 6px;
  border-radius: 7px;
  background: ${({ theme }) => theme.colors.foreground};
  border: 1px solid ${({ theme }) => theme.colors.lightLineDivider};
`;

const StepButton = styled.button`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  margin: 0;
  padding: 2px;
  min-width: 18px;
  min-height: 18px;
  border: none;
  border-radius: 3px;
  background: transparent;
  color: ${({ theme }) => theme.colors.primaryText};
  cursor: pointer;
  line-height: 0;

  &:disabled {
    opacity: 0.35;
    cursor: not-allowed;
  }

  &:hover:not(:disabled) {
    color: ${({ theme }) => theme.colors.secondaryLink};
    background: ${({ theme }) => theme.colors.background};
  }
`;

const ValueInput = styled.input`
  box-sizing: border-box;
  width: 28px;
  min-width: 16px;
  margin: 0;
  padding: 0;
  border: none;
  outline: none;
  background: transparent;
  text-align: center;
  font-size: 12px;
  font-weight: 600;
  font-variant-numeric: tabular-nums;
  color: ${({ theme }) => theme.colors.primaryText};
  appearance: textfield;
  -moz-appearance: textfield;

  &::-webkit-outer-spin-button,
  &::-webkit-inner-spin-button {
    appearance: none;
    margin: 0;
  }
`;

const SecondaryToggle = styled.button<{ $active: boolean }>`
  display: inline-flex;
  align-items: center;
  margin: 0;
  padding: 3px 8px;
  border-radius: 7px;
  border: 1px solid
    ${({ theme, $active }) =>
      $active ? theme.colors.primaryBase : theme.colors.lightLineDivider};
  background: ${({ theme, $active }) =>
    $active ? theme.colors.highlights : theme.colors.foreground};
  color: ${({ theme, $active }) =>
    $active ? theme.colors.primaryBase : theme.colors.secondaryText};
  font-size: 11px;
  font-weight: 600;
  line-height: 1.2;
  cursor: pointer;
  white-space: nowrap;

  &:hover {
    color: ${({ theme }) => theme.colors.primaryBase};
  }
`;

const Subtitle = styled.span`
  display: block;
  margin-top: 2px;
  font-size: 11px;
  line-height: 1.25;
  color: ${({ theme }) => theme.colors.secondaryText};
`;

type GroupChild = {
  type?: string;
  id?: string;
  visible?: boolean;
  customLayerParameters?: Record<string, string>;
  refresh?: () => void;
};

function groupChildren(layer: GISStateLayer): GroupChild[] {
  const group = gisLayerFor(layer) as
    | { layers?: { toArray?: () => GroupChild[] } }
    | undefined;
  return group?.layers?.toArray?.() ?? [];
}

function findCompanionWms(layer: GISStateLayer) {
  const children = groupChildren(layer);
  return (
    children.find(child => child.id === `${layer.id}-raster`) ||
    children.find(child => child.type === 'wms') ||
    null
  );
}

function findSecondaryCompanionWms(layer: GISStateLayer) {
  const secondaryId = `${layer.id}${COMPANION_SECONDARY_RASTER_ID_SUFFIX}`;
  return groupChildren(layer).find(child => child.id === secondaryId) || null;
}

function applyStackingOrder(
  wms: { customLayerParameters?: Record<string, string>; refresh?: () => void },
  order: number,
) {
  wms.customLayerParameters = {
    tiled: 'true',
    ...wms.customLayerParameters,
    CQL_FILTER: `stackingorder=${order}`,
  };
  wms.refresh?.();
}

type CompanionLevelPickerProps = {
  layer: GISStateLayer;
};

function defaultStackingOrderFor(layer: GISStateLayer): number {
  const configured =
    layer.service?.attributes?.raster_companion?.default_stacking_order;
  return Number.isInteger(configured) ? (configured as number) : 0;
}

function secondaryCompanionFor(layer: GISStateLayer) {
  const secondary = layer.service?.attributes?.raster_companion?.secondary_cql;
  const title = secondary?.title?.trim();
  const cqlFilter = secondary?.cql_filter?.trim();
  if (!cqlFilter) return null;
  return {
    title: title || 'Secondary',
    cqlFilter,
  };
}

/**
 * Stacking-order control for WFS + WMS companion layers, with an optional
 * secondary-CQL toggle (e.g. MACRO) on the same row.
 */
export const CompanionLevelPicker = ({ layer }: CompanionLevelPickerProps) => {
  const secondary = secondaryCompanionFor(layer);
  const [order, setOrder] = useState(() => defaultStackingOrderFor(layer));
  const [draft, setDraft] = useState(() =>
    String(stackingOrderToDisplayLevel(defaultStackingOrderFor(layer))),
  );
  const [secondaryOn, setSecondaryOn] = useState(false);

  useEffect(() => {
    setDraft(String(stackingOrderToDisplayLevel(order)));
  }, [order]);

  useEffect(() => {
    const wms = findCompanionWms(layer);
    if (!wms) return;
    applyStackingOrder(wms, order);
  }, [layer, order]);

  useEffect(() => {
    if (!secondary) return;
    const secondaryWms = findSecondaryCompanionWms(layer);
    if (!secondaryWms) return;
    secondaryWms.visible = secondaryOn;
  }, [layer, secondary, secondaryOn]);

  const commitDraft = () => {
    const parsed = Number(draft.trim());
    const nextOrder = displayLevelToStackingOrder(parsed);
    if (nextOrder === null) {
      setDraft(String(stackingOrderToDisplayLevel(order)));
      return;
    }
    setOrder(nextOrder);
  };

  return (
    <Wrapper>
      <Row>
        <Label>Level</Label>
        <LevelStepper>
          <StepButton
            type="button"
            disabled={order <= MIN_STACKING_ORDER}
            onClick={() => setOrder(value => value - 1)}
            aria-label="Lower floor level"
          >
            <ChevronDownIcon width={11} height={9} />
          </StepButton>
          <ValueInput
            type="number"
            inputMode="numeric"
            value={draft}
            aria-label="Floor level"
            onChange={e => setDraft(e.currentTarget.value)}
            onBlur={commitDraft}
            onKeyDown={e => {
              if (e.key === 'Enter') {
                e.currentTarget.blur();
              }
            }}
          />
          <StepButton
            type="button"
            onClick={() => setOrder(value => value + 1)}
            aria-label="Higher floor level"
          >
            <ChevronUpIcon width={11} height={9} />
          </StepButton>
        </LevelStepper>
        {secondary ? (
          <SecondaryToggle
            type="button"
            $active={secondaryOn}
            aria-pressed={secondaryOn}
            aria-label={`Toggle ${secondary.title}`}
            onClick={() => setSecondaryOn(value => !value)}
          >
            {secondary.title}
          </SecondaryToggle>
        ) : null}
      </Row>
      <Subtitle>
        Buildings vary in height. If one looks empty, try a lower level.
      </Subtitle>
    </Wrapper>
  );
};

export function hasWmsCompanion(layer: GISStateLayer): boolean {
  const companion = layer.service?.attributes?.raster_companion;
  return Boolean(companion?.enabled && companion.protocol !== 'wmts');
}
